import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import User from '../components/user';
import WhatsApp from '../components/whatsApp';
import TestUser from '../components/testUser';
import Gala from '../components/gala';

// ============ KONFIG DLA CF (Cloud Functions) ============
// Ustaw w .env: REACT_APP_CF_BASE=https://europe-central2/<PROJECT-ID>.cloudfunctions.net
const CF_BASE = "https://europe-central2-modit-system.cloudfunctions.net";

// KOLEJNOŚĆ PRÓB:
// 1) getUserPaymentStatus -> (preferowany szybki boolean)
// 2) getUserLatestPayments -> (ostatnie płatności / faktury)
// 3) getUserSubscriptions  -> (fallback)
const PAYMENT_ENDPOINTS = [
  "getUserPaymentStatus",
  "getUserLatestPayments",
  "getUserSubscriptions",
];

// statusy subskrypcji traktowane jako nieopłacone
const SUB_UNPAID = new Set(["past_due","unpaid","incomplete","incomplete_expired"]);
// statusy faktur traktowane jako "OK"
const INVOICE_OK = new Set(["paid","void"]);

// wspólny parser -> boolean (czy ma jakieś nieopłacone)
const inferUnpaidFromResponse = (resp) => {
  try {
    // 1) getUserPaymentStatus może zwracać wprost boolean:
    if (typeof resp?.hasUnpaid === "boolean") return resp.hasUnpaid;

    // albo np. status agregowany ("paid"/"unpaid"/"ok"/itp.)
    if (typeof resp?.status === "string") {
      const s = resp.status.toLowerCase();
      if (s === "paid" || s === "ok" || s === "clear") return false;
      if (s === "unpaid" || s === "past_due" || s === "incomplete") return true;
    }

    // 2) getUserLatestPayments może zwracać: { payments: [...] } lub { invoices: [...] }
    if (Array.isArray(resp?.payments)) {
      // payments[i].status -> traktujemy jak status faktury
      return resp.payments.some(p => !INVOICE_OK.has(String(p?.status || "").toLowerCase()));
    }
    if (Array.isArray(resp?.invoices)) {
      return resp.invoices.some(inv => !INVOICE_OK.has(String(inv?.status || "").toLowerCase()));
    }

    // 3) getUserSubscriptions (fallback)
    if (Array.isArray(resp?.subscriptions)) {
      return resp.subscriptions.some(sub => SUB_UNPAID.has(String(sub?.status || "").toLowerCase()));
    }
  } catch (e) {
    console.warn("inferUnpaidFromResponse error:", e);
  }
  return false;
};

// pobranie flagi hasUnpaid dla danego stripeId (po kolei: status -> latest -> subs)
const fetchUnpaidFlagFor = async (stripeId) => {
  if (!CF_BASE || !stripeId) return { hasUnpaid: false };

  for (const fnName of PAYMENT_ENDPOINTS) {
    const url = `${CF_BASE}/${fnName}`;
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type":"application/json" },
        body: JSON.stringify({ stripeId }),
      });
      if (!res.ok) continue;
      const data = await res.json().catch(() => ({}));
      const hasUnpaid = inferUnpaidFromResponse(data);
      return { hasUnpaid };
    } catch (e) {
      // spróbuj kolejny endpoint
    }
  }
  return { hasUnpaid: false };
};

// >>> NOWE: prosta funkcja sprawdzająca, czy jest JAKAKOLWIEK faktura w statusie "open"
const fetchHasOpenInvoiceFor = async (stripeId) => {
  if (!CF_BASE || !stripeId) return { hasOpen: false };
  const url = `${CF_BASE}/getUserLatestPayments`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type":"application/json" },
      body: JSON.stringify({ stripeId }),
    });
    if (!res.ok) return { hasOpen: false };
    const data = await res.json().catch(() => ({}));

    const list = Array.isArray(data?.payments) ? data.payments
               : Array.isArray(data?.invoices) ? data.invoices
               : [];

    const hasOpen = list.some(inv => String(inv?.status || "").toLowerCase() === "open");
    return { hasOpen };
  } catch {
    return { hasOpen: false };
  }
};

export default function Lists({isToogle}) {
  const [users, setUsers] = useState("");
  const [groups, setGroups] = useState("");
  const [locations, setLocations] = useState("");

  // ------ Dane ------
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users2026"), (snapshot) => {
      const data = snapshot.docs.map((doc) => doc.data()) || [];
      const clients = [];
      for (let i = 0; i < data.length; i++) {
        const sub = Object.values(data[i]?.subUsers || {});
        for (let x = 0; x < sub.length; x++) {
          clients.push({ uid: data[i]?.uid, acc: data[i], user: sub[x] });
        }
      }
      setUsers(clients);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const TARGET_ID = "SPdqELFz2ovWSn8eFyws";
    const ref = doc(db, "groups", TARGET_ID);
    const unsub = onSnapshot(
      ref,
      (snapshot) => {
        if (!snapshot.exists()) {
          setGroups([]);
          return;
        }
        const data = snapshot.data() || {};
        const groupsArr = Object.values(data).filter(Boolean);
        setGroups(groupsArr);
      },
      () => setGroups([])
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "locations"), (snapshot) => {
      const data = snapshot.docs.map((doc) => doc.data()) || [];
      const l = [];
      data.forEach((location) => {
        l.push(...Object.values(location || {}));
      });
      setLocations(l);
    });
    return () => unsub();
  }, []);

  // ------ UI stany ------
  const [searcher, setSearcher] = useState("");
  const [actualGroup, setActualGroup] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    whatsapp: "",       // "", "green", "send", "no", "empty"
    missingDocs: false, // brak wymaganego podpisu
    weekdays: [],       // [1..7]
    unpaid: false,      // brak płatności (ogólnie)
    noOpenInvoice: false, // >>> NOWE: brak otwartej faktury (Open)
    familyOnly: false,
    multiGroupOnly: false,
    noTicketAndNoTours: false,
  });

  // indeks grup po id
  const groupIndex = useMemo(
    () => new Map((groups || []).map(g => [g?.id, g])),
    [groups]
  );

  const numberToPolishWeekday = (num) => {
    const days = {
      1: "Poniedziałek",
      2: "Wtorek",
      3: "Środa",
      4: "Czwartek",
      5: "Piątek",
      6: "Sobota",
      7: "Niedziela"
    };
    return days[num] || "Niepoprawny numer dnia (1–7)";
  };

  const LOCATION_CITY_MAP = {
    "1ZYzvEaHWegTVyNk3SqY": "Jastrzębie",
    "PPn4NQOOWZiQo9iOtJPi": "Jastrzębie",
    "jw3A9tNVxgEFSKSW2hHf": "Jastrzębie",
    "pncNRKJn9ByLBqUoVQ5W": "Jastrzębie",
    "uSEC7WdsFajEyyHxcOFe": "Oława",
  };

  const cityCounts = useMemo(() => {
    if (!users || !groups) return {};
    const groupIndexLocal = new Map((groups || []).map(g => [g?.id, g]));
    const counts = {};
    users.forEach(({ user }) => {
      const citiesForUser = new Set();
      (user?.groups || []).forEach(gid => {
        const grp = groupIndexLocal.get(gid);
        const locId = grp?.location_id;
        if (!locId) return;
        const city = LOCATION_CITY_MAP[locId] || "Wrocław";
        citiesForUser.add(city);
      });
      citiesForUser.forEach(city => {
        counts[city] = (counts[city] || 0) + 1;
      });
    });
    return counts;
  }, [users, groups]);

  const hasAnyMissingRequiredDoc = (user) => {
    const docs = user?.docs || {};
    const required = Array.isArray(docs?.required) ? docs.required : [];
    if (required.length === 0) return false;
    return required.some((k) => docs?.[k]?.signed !== true);
  };

  const userWeekdaysSet = (user) => {
    const set = new Set();
    (user?.groups || []).forEach(gid => {
      const grp = groupIndex.get(gid);
      const d = grp?.days || [];
      d.forEach(one => {
        if (one?.weekDay) set.add(one.weekDay);
      });
    });
    return set;
  };

  // ====== MAPA OPŁAT: uid -> bool (hasUnpaid) ======
  const [hasUnpaidByUid, setHasUnpaidByUid] = useState({}); // { [uid]: true/false }
  const [loadingPayments, setLoadingPayments] = useState(false);

  // pobierz flagi dla wszystkich bieżących users (gdy lista się zmienia)
  useEffect(() => {
    let isCancelled = false;
    const run = async () => {
      if (!Array.isArray(users) || users.length === 0) {
        setHasUnpaidByUid({});
        return;
      }
      setLoadingPayments(true);

      // unikalne (uid -> stripeId) z konta głównego
      const uniq = new Map();
      users.forEach(u => {
        const key = u?.uid;
        const stripeId = u?.acc?.stripeId;
        if (!key || !stripeId) return;
        if (!uniq.has(key)) uniq.set(key, stripeId);
      });

      const entries = Array.from(uniq.entries());
      const results = {};
      const BATCH = 10;
      for (let i = 0; i < entries.length; i += BATCH) {
        const slice = entries.slice(i, i + BATCH);
        const promises = slice.map(async ([uid, stripeId]) => {
          const { hasUnpaid } = await fetchUnpaidFlagFor(stripeId);
          results[uid] = !!hasUnpaid;
        });
        await Promise.all(promises);
        if (isCancelled) return;
      }

      if (!isCancelled) {
        setHasUnpaidByUid(results);
      }
      setLoadingPayments(false);
    };
    run();
    return () => { isCancelled = true; };
  }, [users]);

  // ====== NOWE: MAPA otwartych faktur: uid -> bool (hasOpenInvoice) ======
  const [hasOpenInvoiceByUid, setHasOpenInvoiceByUid] = useState({}); // { [uid]: true/false }
  const [loadingOpenInvoices, setLoadingOpenInvoices] = useState(false);

  const hasEntryPrice = (subUser, groupId /* opcjonalne */) => {
  const prices = subUser?.prices;
  if (!prices || typeof prices !== "object") return false;

  const check = (v) =>
    (typeof v === "string" && (v === "entry" || v.includes("entry"))) ||
    (v && typeof v === "object" && (v.type === "entry" || v.mode === "entry"));

  if (groupId) {
    const v = prices[groupId];
    return check(v);
  }
  return Object.values(prices).some(check);
};

  useEffect(() => {
    let isCancelled = false;
    const run = async () => {
      if (!Array.isArray(users) || users.length === 0) {
        setHasOpenInvoiceByUid({});
        return;
      }
      setLoadingOpenInvoices(true);

      // unikalne (uid -> stripeId)
      const uniq = new Map();
      users.forEach(u => {
        const key = u?.uid;
        const stripeId = u?.acc?.stripeId;
        if (!key || !stripeId) return;
        if (!uniq.has(key)) uniq.set(key, stripeId);
      });

      const entries = Array.from(uniq.entries());
      const results = {};
      const BATCH = 10;
      for (let i = 0; i < entries.length; i += BATCH) {
        const slice = entries.slice(i, i + BATCH);
        const promises = slice.map(async ([uid, stripeId]) => {
          const { hasOpen } = await fetchHasOpenInvoiceFor(stripeId);
          results[uid] = !!hasOpen;
        });
        await Promise.all(promises);
        if (isCancelled) return;
      }

      if (!isCancelled) {
        setHasOpenInvoiceByUid(results);
      }
      setLoadingOpenInvoices(false);
    };
    run();
    return () => { isCancelled = true; };
  }, [users]);

  // toggle dnia tygodnia
  const toggleWeekday = (dayNum) => {
    setFilters(prev => {
      const arr = Array.isArray(prev.weekdays) ? [...prev.weekdays] : [];
      const idx = arr.indexOf(dayNum);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(dayNum);
      return { ...prev, weekdays: arr };
    });
  };

  const getUserGalaStatus = (u) => {
  const prices = u?.user?.prices;
  const gala = u?.user?.gala;

  if (!prices || typeof prices !== "object") return "black";

  const groupIds = Object.keys(prices);
  if (!groupIds.length) return "black";

  if (!gala || typeof gala !== "object") return "black";

  const statuses = groupIds.map((groupId) => gala[groupId]);

  const isNone = (v) => v === undefined || v === null || v === "" || v === "none";
  const allNone = statuses.every((v) => isNone(v));
  if (allNone) return "black";

  const allTrue = statuses.every((v) => v === true);
  if (allTrue) return "green";

  const allFalse = statuses.every((v) => v === false);
  if (allFalse) return "red";

  return "yellow";
};

// async function fetchUsersWithoutActiveOrQueuedBatch(uids) {
//   const url = `${CF_BASE}/findUsersWithoutActiveOrQueuedSubscription`;
//   const res = await fetch(url, {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({ uids }),
//     // keepalive: true, // opcjonalnie
//   });
//   const text = await res.text();
//   let data = {};
//   try { data = JSON.parse(text); } catch {}
//   if (!res.ok) {
//     console.error('CF error', res.status, text);
//     throw new Error(data?.error || `CF ${res.status}`);
//   }
//   return data;
// }

// async function fetchUsersWithoutActiveOrQueuedAll(allUids) {
//   const uniq = Array.from(new Set(allUids.filter(Boolean)));
//   const CHUNK = 120;
//   const without = [];
//   const details = {};
//   for (let i = 0; i < uniq.length; i += CHUNK) {
//     const slice = uniq.slice(i, i + CHUNK);
//     const data = await fetchUsersWithoutActiveOrQueuedBatch(slice);
//     (data?.withoutActiveOrQueued || []).forEach(x => without.push(x));
//     Object.assign(details, data?.details || {});
//   }
//   return { without, details };
// }

// // === CF helper: findUsersWithoutActiveOrQueuedSubscription ===
// // Wywołanie pojedynczej partii (POST -> CF)
// const callFindWithout = async ({ uids = [], stripeIds = [] }) => {
//   const url = `${CF_BASE}/findUsersWithoutActiveOrQueuedSubscription`;
//   const res = await fetch(url, {
//     method: "POST",
//     headers: { "Content-Type": "application/json" },
//     body: JSON.stringify(
//       stripeIds.length ? { stripeIds } : { uids } // wysyłamy JEDNO z pól
//     ),
//   });

//   const text = await res.text();
//   let data = {};
//   try { data = JSON.parse(text); } catch (_) {}

//   if (!res.ok) {
//     console.error("CF error", res.status, text);
//     throw new Error(data?.error || `CF ${res.status}`);
//   }
//   return data; // { ok, totalChecked, withoutActiveOrQueued, details }
// };

// // Batchowanie po 120 szt. (bezpiecznie względem timeoutów i rate limitów)
// // Zachowujemy STARĄ NAZWĘ, żeby nie zmieniać reszty kodu:
// const fetchUsersWithoutActiveOrQueued = async (idsArray) => {
//   const arr = Array.isArray(idsArray) ? idsArray.filter(Boolean) : [];
//   const uniq = Array.from(new Set(arr));

//   // auto-detekcja, czy dostałem UID-y czy stripeId (cus_*)
//   const looksLikeStripeId = (v) => typeof v === "string" && v.startsWith("cus_");
//   const isStripeIds = uniq.every(looksLikeStripeId);

//   const CHUNK = 120;
//   const without = [];
//   const details = {};

//   for (let i = 0; i < uniq.length; i += CHUNK) {
//     const slice = uniq.slice(i, i + CHUNK);
//     const data = await callFindWithout(
//       isStripeIds ? { stripeIds: slice } : { uids: slice }
//     );

//     (data?.withoutActiveOrQueued || []).forEach((row) => without.push(row));
//     Object.assign(details, data?.details || {});
//   }

//   return { without, details };
// };


// const [noSubByUid, setNoSubByUid] = useState({}); // { [uid]: true/false }
// const [loadingNoSubs, setLoadingNoSubs] = useState(false);

// // kiedy users się zmienią – pobierz listę uid i zapytaj CF:
// useEffect(() => {
//   let cancel = false;
//   (async () => {
//     if (!Array.isArray(users) || users.length === 0) { setNoSubByUid({}); return; }
//     setLoadingNoSubs(true);
//     const uids = Array.from(new Set(users.map(u => u?.uid).filter(Boolean)));
//     const { without } = await fetchUsersWithoutActiveOrQueued(uids);
//     if (cancel) return;
//     const map = {};
//     without.forEach(row => { if (row?.uid) map[row.uid] = true; });
//     setNoSubByUid(map);
//     setLoadingNoSubs(false);
//   })();
//   return () => { cancel = true; };
// }, [users]);

const hasSiblingsInAccount = (u) => {
  const subUsers = Object.values(u?.acc?.subUsers || {});
  return subUsers.length > 1;
};

const hasMoreThanOneGroup = (u) => {
  return Array.isArray(u?.user?.groups) && u.user.groups.length > 1;
};

const hasNoTicketAndNoTours = (u) => {
  const hasTicketField = Object.prototype.hasOwnProperty.call(u?.user || {}, "ticket");
  const hasToursField = Object.prototype.hasOwnProperty.call(u?.user || {}, "tours");

  return !hasTicketField && !hasToursField;
};


  // ====== Lista użytkowników (z filtrami) ======
  const userList = () => {
    if (!users) return;

    let filtered = users;

    const normalizePhone = (value = "") => String(value).replace(/\D/g, "");

    const trimmedSearch = (searcher || "").trim();
    const normalizedSearch = trimmedSearch.toLowerCase();
const normalizedSearchNoSpaces = normalizedSearch.replace(/\s+/g, "");
const isEmailSearch = normalizedSearch.includes("@");

if (trimmedSearch) {
  const srch = trimmedSearch.toUpperCase();

  // jeżeli wpisano mail, szukamy WYŁĄCZNIE po mailu
  if (trimmedSearch.includes("@")) {
    const exactEmail = trimmedSearch.toLowerCase().trim().replace(/\s+/g, "");

    filtered = filtered?.filter((u) => {
      const email = String(u?.acc?.email || "").toLowerCase().trim();
      return email === exactEmail;
    });
  } else {
    const srchPhone = normalizePhone(trimmedSearch);

    filtered = filtered?.filter((u) => {
      const phoneUser = normalizePhone(u?.user?.phone);
      const phoneParent = normalizePhone(u?.acc?.parent?.phone);

      return (
        u?.user?.displayName?.toUpperCase()?.includes(srch) ||
        u?.acc?.parent?.displayName?.toUpperCase()?.includes(srch) ||
        u?.acc?.email?.toUpperCase()?.includes(srch) ||
        u?.acc?.uid?.toUpperCase()?.includes(srch) ||
        u?.user?.pesel?.toUpperCase()?.includes(srch) ||
        u?.user?.dateOfBirth?.toUpperCase()?.includes(srch) ||
        (srchPhone && phoneUser.includes(srchPhone)) ||
        (srchPhone && phoneParent.includes(srchPhone))
      );
    });
  }
}

    // 2) filtr po grupie – tylko gdy search jest pusty
    if (!trimmedSearch && actualGroup) {
      filtered = filtered?.filter(u => u?.user?.groups?.includes(actualGroup));
    }

    // 3) WhatsApp
    if (filters?.whatsapp) {
      filtered = filtered?.filter(u =>
        filters.whatsapp === "empty"
          ? (u?.user?.whatsApp === "" || u?.user?.whatsApp === undefined)
          : (u?.user?.whatsApp === filters.whatsapp)
      );
    }

  if (filters?.gala) {
    filtered = filtered.filter((u) => getUserGalaStatus(u) === filters.gala);
  }

    if (filters?.extermin) {
      filtered = filtered?.filter(u => u?.user?.extermination?.status === "active" 
      );
    }

    // 4) Braki podpisów
    if (filters?.missingDocs) {
      filtered = filtered?.filter(u => hasAnyMissingRequiredDoc(u?.user));
    }

    // 5) Dni tygodnia
    if (filters?.weekdays?.length > 0) {
      const want = new Set(filters.weekdays);
      filtered = filtered?.filter(u => {
        const uSet = userWeekdaysSet(u?.user);
        for (const d of want) if (uSet.has(d)) return true;
        return false;
      });
    }

  //       if (filters?.noSubscription) {
  //   filtered = filtered?.filter(u => noSubByUid[u?.uid] === true);
  // }

    // 6) Brak płatności (ogólnie)
    if (filters?.unpaid) {
      filtered = filtered?.filter(u => hasUnpaidByUid[u?.uid] === true);
    }

    // 7) >>> NOWE: Brak otwartej faktury (Open)
    if (filters?.noOpenInvoice) {
      // pokazujemy tych, którzy NIE mają faktury w statusie 'open'
      filtered = filtered?.filter(u => hasOpenInvoiceByUid[u?.uid] === false);
    }

    if (filters?.entry) {
      filtered = filtered?.filter(u => hasEntryPrice(u?.user));
    }

    if (filters?.familyOnly) {
  filtered = filtered?.filter((u) => hasSiblingsInAccount(u));
}

if (filters?.multiGroupOnly) {
  filtered = filtered?.filter((u) => hasMoreThanOneGroup(u));
}

if (filters?.noTicketAndNoTours) {
  filtered = filtered?.filter((u) => hasNoTicketAndNoTours(u));
}

    // sort
    filtered = filtered?.sort((a, b) => {
      const nameA = a?.user?.displayName?.toUpperCase() || "";
      const nameB = b?.user?.displayName?.toUpperCase() || "";
      return nameA.localeCompare(nameB);
    });

    return filtered?.map(u => (
      <React.Fragment key={`${u?.uid}-${u?.user?.index || ""}`}>
        <User u={u} locations={locations} groups={groups}/>
      </React.Fragment>
    ));
  };

  return (
    <div className={isToogle ? "side-toogle-margin" : "side-margin"} style={{display: "flex", justifyContent: "flex-start", alignItems: "flex-start", flexDirection: "column", minHeight: "100vh", overflowY: "scroll", gap: "15px", paddingTop: "20px", paddingBottom: "20px"}}>
      <div style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", width: "100%", gap: "20px"}}>
        <div style={{display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", position: "relative", width: "100%", gap: '5px', maxWidth: "800px"}}>
          <div className="mobile-column" style={{display: "flex", justifyContent: "center", alignItems: "center", position: "relative", width: "100%", gap: '5px'}}>
            <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "flex-start", position: "relative", width: "60%", maxWidth: "480px"}}>
              <input
                value={searcher}
                type="text"
                className="searcher-input"
                placeholder="Wyszukaj klienta"
                style={{height: "20px", padding: "7px", width: "100%", outline: "none", fontWeight: "bold", backgroundColor: "rgb(0,0,0,0)", color: "rgb(70,70,70)", fontSize: "12px", borderRadius: "5px", paddingLeft: "35px", border: "1px solid rgb(30,30,30,0.25)"}}
                onChange={(e) => setSearcher(e?.target?.value)}
              />
              <img src="searcher-blue.png" style={{width: "15px", position: "absolute", top: "11px", left: '3%'}} alt="" />
            </div>
            <div style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", width: "400px", gap: "10px"}}>
              <select
                className='searcher'
                value={actualGroup}
                onChange={(e) => setActualGroup(e.target.value)}
                style={{height: "37.5px", padding: "7px", maxWidth: "320px", outline: "none", fontWeight: "bold", backgroundColor: "rgb(0,0,0,0)", border: "1px solid rgb(30,30,30,0.25)", color: "rgb(100,100,100)", fontSize: "12px", borderRadius: "5px"}}
              >
                <option value={""} key={-1}>Wybierz grupę</option>
                {(groups && locations) && Object.values(groups)
                  ?.sort((a, b) => {
                    const nameA = locations?.find(x => x?.id === a?.location_id)?.name || "";
                    const nameB = locations?.find(x => x?.id === b?.location_id)?.name || "";
                    return nameA.localeCompare(nameB);
                  })
                  ?.map((group, index) => (
                    <option value={group?.id} key={index}>
                      {locations?.find(x => x?.id === group?.location_id)?.name} - {
                        group?.days?.[0]?.custom_name
                          ? group?.days[0]?.custom_name
                          : `${numberToPolishWeekday(group?.days?.[0]?.weekDay)} - ${group?.days?.[0]?.startTime} - ${group?.days?.[0]?.endTime} - ${group?.type}`
                      }
                    </option>
                ))}
              </select>
              {/* PRZYCISK FILTRÓW */}
              <div
                style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", height: "20px", width: "20px", borderRadius: "5px", backgroundColor: isFilterOpen ? "#0045C7" : "rgb(0,0,0,0)", padding: "6px", border: "2px solid #0045C7", transition: "0.6s", cursor:"pointer"}}
                onClick={() => setIsFilterOpen(v => !v)}
              >
                <img src={isFilterOpen ? "filter.png" : "filter_blue.png"} style={{width: "24px"}} alt="" />
              </div>
            </div>
          </div>

          {/* PANEL FILTRÓW */}
          {isFilterOpen && 
            <div style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", position: "absolute", bottom: "-500px", right: "0", width: "360px", padding: "10px", backgroundColor: "rgb(255,255,255)", border: "1px solid rgb(30,30,30,0.25)", borderRadius: "5px", gap: "10px"}}>
              <h3 style={{color: "rgb(45,45,45)", fontSize: "12px"}}>Filtry:</h3>

              {/* WhatsApp */}
              <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "10px", paddingLeft: "5px"}}>
                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", padding: "3px", borderRadius: "3px", borderBottom: filters?.whatsapp === "green" ? "5px solid #0045C7" : "5px solid transparent", transition: "0.4s"}}>
                  <WhatsApp wth={"27px"} color="#2e9428" curs={"pointer"} clickFunc={() => setFilters(f => ({...f, whatsapp: f.whatsapp === "green" ? "" : "green"}))}/>
                </div>
                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", padding: "3px", borderRadius: "3px", borderBottom: filters?.whatsapp === "send" ? "5px solid #0045C7" : "5px solid transparent", transition: "0.4s"}}>
                  <WhatsApp wth={"27px"} color="#e0b531" curs={"pointer"} clickFunc={() => setFilters(f => ({...f, whatsapp: f.whatsapp === "send" ? "" : "send"}))}/>
                </div>
                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", padding: "3px", borderRadius: "3px", borderBottom: filters?.whatsapp === "no" ? "5px solid #0045C7" : "5px solid transparent", transition: "0.4s"}}>
                  <WhatsApp wth={"27px"} color="#DC143C" curs={"pointer"} clickFunc={() => setFilters(f => ({...f, whatsapp: f.whatsapp === "no" ? "" : "no"}))}/>
                </div>
                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", padding: "3px", borderBottom: filters?.whatsapp === "empty" ? "5px solid #0045C7" : "5px solid transparent", borderRadius: "3px", transition: "0.4s"}}>
                  <WhatsApp wth={"27px"} color="rgb(30,30,30)" curs={"pointer"} clickFunc={() => setFilters(f => ({...f, whatsapp: f.whatsapp === "empty" ? "" : "empty"}))}/>
                </div>
              </div>

              <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "10px", paddingLeft: "5px"}}>
                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", padding: "3px", borderRadius: "3px", borderBottom: filters?.gala === "green" ? "5px solid #0045C7" : "5px solid transparent", transition: "0.4s"}}>
                  <Gala
                    wth={"27px"}
                    color="#2e9428"
                    curs={"pointer"}
                    clickFunc={() => setFilters(f => ({ ...f, gala: f.gala === "green" ? "" : "green" }))}
                  />
                </div>

                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", padding: "3px", borderRadius: "3px", borderBottom: filters?.gala === "yellow" ? "5px solid #0045C7" : "5px solid transparent", transition: "0.4s"}}>
                  <Gala
                    wth={"27px"}
                    color="#e0b531"
                    curs={"pointer"}
                    clickFunc={() => setFilters(f => ({ ...f, gala: f.gala === "yellow" ? "" : "yellow" }))}
                  />
                </div>

                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", padding: "3px", borderRadius: "3px", borderBottom: filters?.gala === "red" ? "5px solid #0045C7" : "5px solid transparent", transition: "0.4s"}}>
                  <Gala
                    wth={"27px"}
                    color="#DC143C"
                    curs={"pointer"}
                    clickFunc={() => setFilters(f => ({ ...f, gala: f.gala === "red" ? "" : "red" }))}
                  />
                </div>

                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", padding: "3px", borderBottom: filters?.gala === "black" ? "5px solid #0045C7" : "5px solid transparent", borderRadius: "3px", transition: "0.4s"}}>
                  <Gala
                    wth={"27px"}
                    color="rgb(30,30,30)"
                    curs={"pointer"}
                    clickFunc={() => setFilters(f => ({ ...f, gala: f.gala === "black" ? "" : "black" }))}
                  />
                </div>
              </div>

              <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "10px", paddingLeft: "5px"}}>
                <label style={{display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333"}}>
                  <input
                    type="checkbox"
                    checked={!!filters.extermin}
                    onChange={() => setFilters(f => ({...f, extermin: !filters?.extermin}))}
                  />
                  Wypowiedzenie
                </label>
              </div>

              <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "10px", paddingLeft: "5px"}}>
              <label style={{display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333"}}>
                <input
                  type="checkbox"
                  checked={!!filters.noTicketAndNoTours}
                  onChange={() => setFilters(f => ({...f, noTicketAndNoTours: !f.noTicketAndNoTours}))}
                />
                Ticket = true, brak tours
              </label>
            </div>

              {/* Braki podpisów */}
              <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "10px", paddingLeft: "5px"}}>
                <label style={{display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333"}}>
                  <input
                    type="checkbox"
                    checked={!!filters.missingDocs}
                    onChange={() => setFilters(f => ({...f, missingDocs: !f.missingDocs}))}
                  />
                  Jakikolwiek brak wymaganego podpisu
                </label>
              </div>

              <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "10px", paddingLeft: "5px"}}>
                <label style={{display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333"}}>
                  <input
                    type="checkbox"
                    checked={!!filters.entry}
                    onChange={() => setFilters(f => ({...f, entry: !f?.entry}))}
                  />
                  Wejście jednorazowe
                </label>
              </div>

              <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "10px", paddingLeft: "5px"}}>
                <label style={{display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333"}}>
                  <input
                    type="checkbox"
                    checked={!!filters.familyOnly}
                    onChange={() => setFilters(f => ({...f, familyOnly: !f.familyOnly}))}
                  />
                  Rodzeństwa / rodziny
                </label>
              </div>

              <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "10px", paddingLeft: "5px"}}>
                <label style={{display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333"}}>
                  <input
                    type="checkbox"
                    checked={!!filters.multiGroupOnly}
                    onChange={() => setFilters(f => ({...f, multiGroupOnly: !f.multiGroupOnly}))}
                  />
                  Więcej niż 1 grupa
                </label>
            </div>

             {/* <label style={{display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333"}}>
              <input
                type="checkbox"
                checked={!!filters.noSubscription}
                onChange={() => setFilters(f => ({...f, noSubscription: !f.noSubscription}))}
              />
              Brak subskrypcji (aktywnej/zakolejkowanej)
            </label>
            {loadingNoSubs && <span style={{fontSize:11, color:"#666"}}>sprawdzam…</span>} */}

              {/* Dni tygodnia */}
              <div style={{display:"flex", flexDirection:"column", gap:6, paddingLeft: "5px"}}>
                <div style={{fontSize:12, color:"#444", fontWeight:700}}>Dzień tygodnia:</div>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:6}}>
                  {[1,2,3,4,5,6,7].map((d) => (
                    <label key={d} style={{display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333"}}>
                      <input
                        type="checkbox"
                        checked={filters.weekdays.includes(d)}
                        onChange={() => toggleWeekday(d)}
                      />
                      {numberToPolishWeekday(d)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Brak płatności (ogólnie) */}
              <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "10px", paddingLeft: "5px"}}>
                <label style={{display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333"}}>
                  <input
                    type="checkbox"
                    checked={!!filters.unpaid}
                    onChange={() => setFilters(f => ({...f, unpaid: !f.unpaid}))}
                  />
                  Brak płatności (open/expired/incomplete… bez „void”)
                </label>
                {loadingPayments && <span style={{fontSize:11, color:"#666"}}>odświeżam…</span>}
              </div>

              {/* >>> NOWE: Brak otwartej faktury */}
              <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "10px", paddingLeft: "5px"}}>
                <label style={{display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333"}}>
                  <input
                    type="checkbox"
                    checked={!!filters.noOpenInvoice}
                    onChange={() => setFilters(f => ({...f, noOpenInvoice: !f.noOpenInvoice}))}
                  />
                  Brak otwartej faktury (status: Open)
                </label>
                {loadingOpenInvoices && <span style={{fontSize:11, color:"#666"}}>sprawdzam…</span>}
              </div>
            </div>
          }
        </div>

        {users &&
          <div style={{display: "flex", flexDirection: "row", justifyContent: "space-around", alignItems: "center", border: "1px solid rgb(30,30,30,0.25)", padding: "15px 20px", width: "87.5%", height: "30px", borderRadius: "5px", marginBottom: "5px"}}>
            <div style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
              <h3 style={{fontSize: "14px", color: "rgb(30,30,30)"}}>{userList()?.length}</h3>
              <h3 style={{fontSize: "12px", color: "rgb(30,30,30, 0.6)"}}>Ilość uczestników</h3>
            </div>

            <div style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
              <h3 style={{fontSize: "14px", color: "rgb(30,30,30)"}}>{cityCounts["Wrocław"] || 0}</h3>
              <h3 style={{fontSize: "12px", color: "rgb(30,30,30, 0.6)"}}>Wrocław</h3>
            </div>

            <div style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
              <h3 style={{fontSize: "14px", color: "rgb(30,30,30)"}}>{cityCounts["Oława"] || 0}</h3>
              <h3 style={({fontSize: "12px", color: "rgb(30,30,30, 0.6)"})}>Oława</h3>
            </div>

            <div style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
              <h3 style={{fontSize: "14px", color: "rgb(30,30,30)"}}>{cityCounts["Jastrzębie"] || 0}</h3>
              <h3 style={({fontSize: "12px", color: "rgb(30,30,30, 0.6)"})}>Jastrzębie</h3>
            </div>
          </div>
        }

        {users && userList()}
        {users && <h3 style={{fontSize: "12px", color: "rgb(30,30,30, 0.6)"}}>Ilość wyników: {userList()?.length}</h3>}
      </div>
    </div>
  );
}
